import React, { useEffect, useState } from "react";
import {Button, DatePicker, Form, Input, message, Modal, Select, Steps, Tooltip} from "antd";
import { LeftOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { getConfig } from "../../../../../config";
import ColorReservationCreation from "../../../../internalOrders/colorReservation/colorReservationCreation/ColorReservationCreation";
import { TaskData } from "../../../../../types/TaskData";
import { ViewType } from "../../../../../types/ViewType";
import dayjs from "dayjs";
import ConstructionOrderCreation
    from "../../../../internalOrders/constructionOrder/constructionOrderCreation/ConstructionOrderCreation";
import SystemPlanningOrderCreation
    from "../../../../internalOrders/systemPlanningOrder/systemPlanningOrderCreation/SystemPlanningOrderCreation";
import locale from "antd/es/date-picker/locale/de_DE";

const { Step } = Steps;
const { API_BASE_URL } = getConfig();

interface Task {
    configuration: string;
    taskType: string;
}

interface TransferProductEditWizardProps {
    visible: boolean;
    onClose: () => void;
    selectedProduct: ProductData;
    selectedProject: string;
    tasks: LegacyTask[];
}

const TransferProductEditWizard: React.FC<TransferProductEditWizardProps> = ({
                                                                                 visible,
                                                                                 onClose,
                                                                                 selectedProduct,
                                                                                 selectedProject,
                                                                                 tasks,
                                                                             }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const [intermediateData, setIntermediateData] = useState<any>({});
    const [documentationData, setDocumentationData] = useState({
        systemPlanLink: "",
        colorInfo: "",
        approvalDate: null as string | null,
    });
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [availableColors, setAvailableColors] = useState<string[]>([]);

    useEffect(() => {
        if (!visible) {
            setCurrentStep(0);
            form.resetFields();
            setIntermediateData({});
        }
    }, [visible]);

    useEffect(() => {
        const fetchColors = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get<string[]>(
                    `${API_BASE_URL}/getColorsByProduct?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${selectedProject}&jobCode=${selectedProduct.code}`
                );
                setAvailableColors(response.data);
            } catch (error) {
                console.error("Error fetching color data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        if (selectedProject && selectedProduct.code) {
            fetchColors();
        }
    }, [selectedProject, selectedProduct.code]);

    const isStepComplete = () => {
        const task = tasks[currentStep];
        if (!task) return false;

        switch (task.code) {
            case "DOCUMENTATION_SYSTEM_PLANNING":
                return (
                    documentationData.systemPlanLink.trim() !== "" &&
                    documentationData.approvalDate !== null
                );
            case "MATERIAL_RESERVATION":
            case "COLOR_RESERVATION":
            case "SYSTEM_PLANNING_ASSIGNMENT":
                return !!intermediateData[task.code];
            default:
                return true;
        }
    };

    useEffect(() => {
        const hasDocumentationStep = tasks.some((task) => task.code === "DOCUMENTATION_SYSTEM_PLANNING");
        const hasReservationSteps = tasks.some(
            (task) => task.code === "MATERIAL_RESERVATION" || task.code === "COLOR_RESERVATION"
        );
        const isDocumentationComplete = tasks.some(t => t.code === "DOCUMENTATION_SYSTEM_PLANNING")
            ? documentationData.systemPlanLink.trim() !== "" && documentationData.approvalDate !== null
            : true;

        const isReservationsComplete = tasks
            .filter(t => ["MATERIAL_RESERVATION", "COLOR_RESERVATION"].includes(t.code))
            .every(t => !!intermediateData[t.code]);

        const isSystemPlanningComplete = tasks.some(t => t.code === "SYSTEM_PLANNING_ASSIGNMENT")
            ? !!intermediateData["SYSTEM_PLANNING_ASSIGNMENT"]
            : true;

        setIsSaveDisabled(!(isDocumentationComplete && isReservationsComplete && isSystemPlanningComplete));
    }, [documentationData, intermediateData, tasks]);

    const handleNext = () => {
        if (isStepComplete()) {
            setCurrentStep((prev) => Math.min(prev + 1, tasks.length - 1));
        } else {
            message.warning("Bitte fülle alle erforderlichen Felder aus.");
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleIntermediateSave = (taskType: string, description: string, data: any) => {
        setIntermediateData((prev: any) => ({
            ...prev,
            [taskType]: data,
        }));
        message.success(`Daten für '${description}' zwischengespeichert`);
    };

    const handleFinalSave = async () => {
        Modal.confirm({
            width: 600,
            title: "Willst Du die Daten speichern?",
            content: "Die Änderungen werden gespeichert und der Wizard geschlossen.",
            okText: "Ja",
            cancelText: "Abbrechen",
            onOk: async () => {
                setIsLoading(true);
                try {
                    const { SYSTEM_PLANNING_ASSIGNMENT, ...reservationData } = intermediateData;
                    const payload = {
                        projectNo: selectedProject,
                        productCode: selectedProduct.code,
                        documentation: documentationData,
                        reservations: Object.values(reservationData),
                        systemPlanningOrder: SYSTEM_PLANNING_ASSIGNMENT ?? null,
                    };
                    await axios.post(`${API_BASE_URL}/saveTransferProjectProductStep`, payload);
                    message.success("Daten erfolgreich gespeichert.");
                    onClose();
                } catch (error) {
                    message.error("Fehler beim Speichern der Daten.");
                } finally {
                    setIsLoading(false);
                }
            },
        });
    };

    const handleValidatedInput = (value: string): string => {
        if (value === "h") {
            return "helios://";
        } else if (value.startsWith("helios://") || validateHeliosUrl(value)) {
            return value;
        } else {
            message.error("Bitte gebe eine gültige Helios-URL an");
            return ""; // Reset to an empty string if invalid
        }
    };

    const validateHeliosUrl = (value: string): boolean => {
        return value.startsWith("helios://") && value.length > "helios://".length;
    };

    const renderStepContent = (task: LegacyTask) => {
        if (!task) {
            console.warn("renderStepContent received undefined task!");
            return <p>Kein gültiger Task ausgewählt.</p>;
        }
        switch (task.code) {
            case "DOCUMENTATION_SYSTEM_PLANNING":
                return (
                    <Form form={form} layout="vertical">
                        <Form.Item label="Verlinkung Systemplan">
                            <Input
                                placeholder="Helios-URL"
                                value={documentationData.systemPlanLink}
                                onChange={(e) => {
                                    const newValue = handleValidatedInput(e.target.value);
                                    setDocumentationData((prev) => ({
                                        ...prev,
                                        systemPlanLink: newValue,
                                    }));
                                }}
                            />
                        </Form.Item>
                        <Form.Item label="Freigabe-Datum">
                            <DatePicker
                                locale={locale}
                                format="DD.MM.YYYY"
                                value={documentationData.approvalDate ? dayjs(documentationData.approvalDate, 'DD.MM.YYYY') : null}
                                onChange={(date) =>
                                    setDocumentationData((prev) => ({
                                        ...prev,
                                        approvalDate: date ? date.format('DD.MM.YYYY') : null,
                                    }))
                                }
                            />
                        </Form.Item>
                    </Form>
                );
            case "MATERIAL_RESERVATION":
            case "COLOR_RESERVATION":
                return (
                    <ColorReservationCreation
                        data={{ jobCode: selectedProduct.code } as TaskData}
                        viewType={ViewType.NEW_EXTERNAL}
                        onSave={(data) => handleIntermediateSave(task.code, task.description, data)}
                        projectNo={selectedProject}
                        type={task.code === "MATERIAL_RESERVATION" ? "material" : "color"}
                        externalSystemPlanLink={documentationData.systemPlanLink}
                    />
                );
            case "SYSTEM_PLANNING_ASSIGNMENT":
                return (
                    <SystemPlanningOrderCreation
                        data={{
                            id: 1,
                            jobCode: "STGL1",
                            faz: dayjs().format("YYYY-MM-DD"),
                            fez: dayjs().format("YYYY-MM-DD"),
                            totalBuffer: 10,
                        } as TaskData}
                        viewType={ViewType.NEW_EXTERNAL}
                        projectNo={selectedProject}
                        onSave={(data) => handleIntermediateSave(task.code, task.description, data)}
                        onRefreshTable={() => null}
                    />
                );
            default:
                return <p>Unbekannter Task-Typ: {task.code}</p>;
        }
    };

    return (
        <Modal
            title={`Bearbeiten: Produkt ${selectedProject} | ${selectedProduct.name} | ${selectedProduct.code}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width="80vw"
            styles={{
                body: { height: "80vh", overflowY: "auto", padding: "20px" },
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <Tooltip title="Zurück">
                    <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={currentStep === 0} />
                </Tooltip>
                {tasks.length > 1 && (
                    <Steps current={currentStep} size="default" style={{ flex: 1, margin: "0 16px" }}>
                        {tasks.map((task, index) => (
                            <Step key={index} title={task.description} />
                        ))}
                    </Steps>
                )}
                {currentStep === tasks.length - 1 ? (
                    <Tooltip title={isSaveDisabled ? "Alle Schritte müssen abgeschlossen sein" : "Speichern & Schliessen"}>
                        <Button
                            type="primary"
                            onClick={handleFinalSave}
                            loading={isLoading}
                            disabled={isSaveDisabled}
                            icon={<SaveOutlined />}
                        >
                            {isLoading ? "Speichert..." : "Speichern & Schliessen"}
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip title="Weiter">
                        <Button icon={<RightOutlined />} type="primary" onClick={handleNext} disabled={!isStepComplete()} />
                    </Tooltip>
                )}
            </div>
            <div>{renderStepContent(tasks[currentStep])}</div>
        </Modal>
    );
};

export default TransferProductEditWizard;